import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { ArrowLeft, Plus, Edit, Trash2, MapPin, Phone, Users, Building2 } from 'lucide-react';
import { toast } from 'sonner';

const AdminBranches = () => {
  const navigate = useNavigate();
  const [branches, setBranches] = useState([]);
  const [branchStats, setBranchStats] = useState({});
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    fetchBranches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  const fetchBranches = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/branches`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setBranches(data);
        
        // Fetch stats for each branch
        const statsPromises = data.map(branch => 
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/branches/${branch.branch_id}/stats`, {
            credentials: 'include'
          }).then(res => res.ok ? res.json() : null)
        );
        
        const statsResults = await Promise.all(statsPromises);
        const statsMap = {};
        data.forEach((branch, index) => {
          if (statsResults[index]) {
            statsMap[branch.branch_id] = statsResults[index];
          }
        });
        setBranchStats(statsMap);
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
      toast.error('Error al cargar sucursales');
    } finally {
      setLoading(false);
    }
  };
  
  const handleDeleteBranch = async (branchId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta sucursal?')) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/branches/${branchId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('Sucursal eliminada');
        fetchBranches();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al eliminar sucursal');
      }
    } catch (error) {
      console.error('Error deleting branch:', error);
      toast.error('Error al eliminar sucursal');
    }
  };
  
  return (
    <div className="min-h-screen bg-black page-transition">
      {/* Header */}
      <div className="bg-zinc-950 border-b border-zinc-800 p-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button onClick={() => navigate('/dashboard')} variant="ghost" size="sm" data-testid="back-button">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-2xl font-heading text-white uppercase">SUCURSALES</h1>
          </div>
          
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                data-testid="add-branch-button"
                className="bg-primary hover:bg-[#b20710] text-white rounded-sm font-bold uppercase tracking-wider shadow-[0_0_15px_rgba(229,9,20,0.3)]"
              >
                <Plus className="mr-2 h-4 w-4" />
                NUEVA SUCURSAL
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-950 border-zinc-800 text-white">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl uppercase">Nueva Sucursal</DialogTitle>
                <DialogDescription className="text-zinc-400">
                  Agrega una nueva sucursal para expandir tu academia
                </DialogDescription>
              </DialogHeader>
              <BranchForm onSuccess={() => {
                setIsCreateDialogOpen(false);
                fetchBranches();
              }} />
            </DialogContent>
          </Dialog>
        </div>
      </div>
      
      {/* Content */}
      <div className="max-w-7xl mx-auto p-4 pb-20">
        {loading ? (
          <div className="text-center py-12 text-zinc-500">Cargando...</div>
        ) : branches.length === 0 ? (
          <div className="text-center py-12">
            <Building2 className="h-16 w-16 text-zinc-700 mx-auto mb-4" />
            <h3 className="text-xl font-heading text-white mb-2">Sin Sucursales</h3>
            <p className="text-zinc-500 mb-4">Crea tu primera sucursal para organizar tus alumnos</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {branches.map((branch) => {
              const stats = branchStats[branch.branch_id] || {};
              return (
                <Card 
                  key={branch.branch_id} 
                  className={`bg-zinc-950 border-zinc-800 p-6 hover:border-zinc-700 transition-colors ${!branch.is_active ? 'opacity-50' : ''}`}
                  data-testid={`branch-card-${branch.branch_id}`}
                >
                  <div className="space-y-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="text-xl font-heading text-white uppercase">{branch.name}</h3>
                        {!branch.is_active && (
                          <span className="text-xs text-yellow-500 uppercase">Inactiva</span>
                        )}
                      </div>
                      <div className="flex space-x-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setSelectedBranch(branch);
                            setIsEditDialogOpen(true);
                          }}
                          data-testid={`edit-branch-${branch.branch_id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteBranch(branch.branch_id)}
                          data-testid={`delete-branch-${branch.branch_id}`}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                    
                    {branch.address && (
                      <div className="flex items-start space-x-2 text-sm">
                        <MapPin className="h-4 w-4 text-zinc-500 mt-0.5" />
                        <span className="text-zinc-400">{branch.address}</span>
                      </div>
                    )}
                    
                    {branch.phone && (
                      <div className="flex items-center space-x-2 text-sm">
                        <Phone className="h-4 w-4 text-zinc-500" />
                        <span className="text-zinc-400">{branch.phone}</span>
                      </div>
                    )}
                    
                    {/* Stats */}
                    <div className="grid grid-cols-2 gap-3 pt-3 border-t border-zinc-800">
                      <div className="text-center">
                        <div className="flex items-center justify-center space-x-1">
                          <Users className="h-4 w-4 text-primary" />
                          <span className="text-2xl font-bold text-white">{stats.total_students || 0}</span>
                        </div>
                        <span className="text-xs text-zinc-500">Alumnos</span>
                      </div>
                      <div className="text-center">
                        <span className="text-2xl font-bold text-green-500">{stats.active_payments || 0}</span>
                        <p className="text-xs text-zinc-500">Pagos Activos</p>
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
      
      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase">Editar Sucursal</DialogTitle>
          </DialogHeader>
          {selectedBranch && (
            <BranchForm 
              branch={selectedBranch}
              onSuccess={() => {
                setIsEditDialogOpen(false);
                setSelectedBranch(null);
                fetchBranches();
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Branch Form Component
const BranchForm = ({ branch, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: branch?.name || '',
    address: branch?.address || '',
    phone: branch?.phone || '',
    is_active: branch?.is_active !== false
  });
  const [loading, setLoading] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    
    setLoading(true);
    
    try {
      const url = branch 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/branches/${branch.branch_id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/branches`;
      
      const method = branch ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        toast.success(branch ? 'Sucursal actualizada' : 'Sucursal creada');
        onSuccess();
      } else {
        throw new Error('Failed to save branch');
      }
    } catch (error) {
      console.error('Error saving branch:', error);
      toast.error('Error al guardar sucursal');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label className="text-white">Nombre *</Label>
        <Input
          required
          value={formData.name}
          onChange={(e) => setFormData({...formData, name: e.target.value})}
          className="bg-zinc-900 border-zinc-800 text-white"
          placeholder="Ej: BKA Sede Central"
        />
      </div>
      
      <div>
        <Label className="text-white">Dirección</Label>
        <Input
          value={formData.address}
          onChange={(e) => setFormData({...formData, address: e.target.value})}
          className="bg-zinc-900 border-zinc-800 text-white"
          placeholder="Ej: Av. Principal #123"
        />
      </div>
      
      <div>
        <Label className="text-white">Teléfono</Label>
        <Input
          value={formData.phone}
          onChange={(e) => setFormData({...formData, phone: e.target.value})}
          className="bg-zinc-900 border-zinc-800 text-white"
          placeholder="Ej: +591 77777777"
        />
      </div>
      
      {branch && (
        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="is_active"
            checked={formData.is_active}
            onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
            className="rounded border-zinc-700"
          />
          <Label htmlFor="is_active" className="text-white cursor-pointer">
            Sucursal activa
          </Label>
        </div>
      )}
      
      <Button 
        type="submit" 
        disabled={loading}
        className="w-full bg-primary hover:bg-[#b20710] text-white rounded-sm font-bold uppercase"
      >
        {loading ? 'Guardando...' : branch ? 'Actualizar' : 'Crear Sucursal'}
      </Button>
    </form>
  );
};

export default AdminBranches;
